import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/* LMM (2026-09-17, Chris): Dezimalkomma -> Punkt fuer den Begriff, der an Shopify
 * geht. Shopifys Tokenizer zerlegt "4,6w" in "4" + "6w" (Treffer: 6-W-Roehren und
 * 4,6-W-Bodeneinbauleuchten), haelt "4.6w" aber als Dezimalzahl zusammen (Treffer:
 * Philips CorePro 4,6-50W) — live gemessen ueber /search/suggest.json. Nur 1-3
 * Ziffern vor und 1-2 Ziffern nach dem Komma (Watt-/Ampere-Dezimale); Artikel-
 * nummernlisten ("1030008088,1030008089") und Tausendertrennung bleiben unberuehrt.
 * Das Eingabefeld selbst bleibt, wie der Kunde getippt hat. Dieselbe Regel liegt
 * zentral in LMMSearchIntent.dezimalNormalisieren (Engine Part 8); der Inline-
 * Ausdruck ist nur der Rueckfall, falls die Engine fehlt. */
function lmmDezimalPunkt(term) {
  const engine = window.LMMSearchIntent;
  if (engine && typeof engine.dezimalNormalisieren === 'function') return engine.dezimalNormalisieren(term);
  return String(term == null ? '' : term).replace(/(^|[^0-9])([0-9]{1,3}),([0-9]{1,2})(?=[^0-9]|$)/g, '$1$2.$3');
}

/* LMM (2026-10-20, AUFGABE:20261020-14): Gezielte Feldsuche fuer 10-stellige
 * Artikelnummern (variants.sku:) und 13-stellige EANs (variants.barcode:) im
 * Dropdown (/search/suggest) und bei Enter-Navigation. Das sichtbare Eingabefeld
 * und sessionStorage behalten die reine Nummer ueber lmmIdentAnzeige. */
function lmmIdentAnzeige(term) {
  const engine = window.LMMSearchIntent;
  if (engine && typeof engine.identAnzeige === 'function') return engine.identAnzeige(term);
  return String(term == null ? '' : term).replace(/^\s*variants\.(?:sku|barcode):\s*/i, '');
}

function lmmFeldSuche(term) {
  const engine = window.LMMSearchIntent;
  if (engine && typeof engine.feldSuche === 'function') return engine.feldSuche(term);
  const dez = lmmDezimalPunkt(term);
  const getrimmt = String(dez || '').trim();
  const kompakt = lmmIdentAnzeige(getrimmt).replace(/[\s\-.\u2010-\u2015]/g, '');
  if (/^[0-9]{10}$/.test(kompakt)) return 'variants.sku:' + kompakt;
  if (/^[0-9]{13}$/.test(kompakt)) return 'variants.barcode:' + kompakt;
  return dez;
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /** LMM: zuletzt erfolgreich gemorphter (getrimmter) Suchbegriff — verhindert
   * Refetch+Morph bei reinen Whitespace-Änderungen (z. B. angehängtem Leerzeichen),
   * die sonst das Dropdown sichtbar zerlegen und neu aufbauen. */
  #lastSearchTerm = '';

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    /*
     * LMM (2026-07-30): Dieser Vorablauf fuer "Zuletzt angesehen" ruft
     * resetSearch() -> #resetSearch() und leerte damit auf JEDER Seite den
     * Suchbegriff, sobald der Besucher schon einmal ein Produkt angesehen
     * hatte. Auf /search?q=... war das Feld deshalb leer, obwohl der Server
     * den Begriff korrekt als value-Attribut ausliefert; zusaetzlich
     * verschwand das Loesch-X (#hideResetButton).
     *
     * Gemessen auf MAIN 203071422801, gleiche URL, gleiches Theme, gleiches
     * Profil — einziger Unterschied die Liste "viewedProducts":
     *   viewedProducts = 4 Produkte -> Feld leer,  X versteckt
     *   viewedProducts = []         -> Feld gefuellt, X sichtbar
     *
     * Ein bereits gefuelltes Feld bleibt jetzt unangetastet. Der Leerzustand
     * mit "Zuletzt angesehen" geht nicht verloren: Er wird geladen, sobald das
     * Feld tatsaechlich leer ist — beim X, beim Leertippen und beim Oeffnen
     * der Suche ohne Begriff (dort laeuft dieselbe #resetSearch-Logik).
     */
    if (this.refs.searchInput && this.refs.searchInput.value) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const exactSearch = window.LMMExactProductSearch;
        if (exactSearch && exactSearch.parse(this.refs.searchInput.value)) {
          event.preventDefault();
          const selected = this.#currentIndex >= 0 ? this.#currentItem : null;
          if (selected instanceof HTMLElement && !selected.hidden && selected.style.display !== 'none' && exactSearch.matches(selected, exactSearch.parse(this.refs.searchInput.value))) {
            selected.querySelector('a')?.click();
            return;
          }
          const exactUrl = exactSearch.destination(this.refs.predictiveSearchResults, this.refs.searchInput.value);
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('type', 'product');
          searchUrl.searchParams.set('q', lmmFeldSuche(this.refs.searchInput.value));
          window.location.href = exactUrl || searchUrl.toString();
          return;
        }
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          // LMM: Enter ohne gewaehlten Vorschlag — derselbe Begriff wie im Dropdown (Dezimalpunkt + SKU/EAN-Feldsuche).
          searchUrl.searchParams.set('type', 'product');
          searchUrl.searchParams.set('q', lmmFeldSuche(this.refs.searchInput.value));
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();

    // LMM: getrimmt unveränderte Query (nur Whitespace getippt/gelöscht) → Ergebnis
    // steht bereits; erneuter Fetch würde identisches Markup morphen und das
    // Dropdown nur flackern/springen lassen.
    if (searchTerm === this.#lastSearchTerm) return;

    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    const requestTerm = lmmFeldSuche(
      /\b(?:pl\s*[- ]?l|pll|2g11|dulux\s+l)\b/i.test(searchTerm)
        ? searchTerm.replace(/\buv\s*[- ]?c\b|\buvc\b/gi, 'entkeimung')
        : searchTerm
    );
    url.searchParams.set('q', requestTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // LMM: Produkte UND Kategorien explizit anfragen. Ohne resources[type] hydriert die
    // Suggest-API weder Collection-Treffer noch product.collections — der serverseitige
    // Kategorien-Fallback bei Artikelnummer-Suchen (lmm-predictive-results.liquid) bliebe leer.
    url.searchParams.set('resources[type]', 'product,collection');

    // LMM: optionale zusätzliche Produkt-Suchfelder (z. B. `tag`), konfiguriert über
    // data-search-fields am <predictive-search-component>. Shopify-Default-Predictive-Felder
    // enthalten `tag` NICHT; ohne dieses Attribut bleibt das Verhalten unverändert (kein Override).
    // Tippfehler werden NICHT ungeprüft an Shopify durchgereicht: gegen die von der
    // Predictive-Search-API erlaubten Felder validieren, trimmen, deduplizieren.
    const ALLOWED_SEARCH_FIELDS = [
      'author',
      'body',
      'product_type',
      'tag',
      'title',
      'variants.barcode',
      'variants.sku',
      'variants.title',
      'vendor',
    ];
    const rawSearchFields = this.dataset.searchFields;
    if (rawSearchFields) {
      const fields = rawSearchFields
        .split(',')
        .map((field) => field.trim())
        .filter((field, index, all) => field && all.indexOf(field) === index)
        .filter((field) => ALLOWED_SEARCH_FIELDS.includes(field));
      if (fields.length) {
        url.searchParams.set('resources[options][fields]', fields.join(','));
      } else {
        console.warn(
          `[lmm-predictive] data-search-fields enthält keine gültigen Felder: "${rawSearchFields}" — ignoriert, Shopify-Default aktiv.`
        );
      }
    }

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);
        this.#lastSearchTerm = searchTerm;

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    this.#lastSearchTerm = '';
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}

/* ===========================================================================
   LMM · Suchbegriff-Gedächtnis je Browser-Tab (Chris 2026-07-28)
   ---------------------------------------------------------------------------
   Der zuletzt eingegebene Suchbegriff bleibt INNERHALB DESSELBEN TABS erhalten,
   wenn der Kunde aus der Suche heraus ein Produkt, eine Kategorie oder eine
   andere Seite öffnet. Damit muss er nach dem Zurückspringen nicht neu tippen.

   Bewusst NICHT verändert: die Klasse oben, die Trefferlogik, die Ziel-URLs und
   die Search-Single-Navigation. Dieser Block hängt ausschließlich zusätzliche
   Listener an — er ruft weder resetSearch() noch #resetSearch() auf und löst
   selbst keine Navigation und keine Suche aus.

   Speicher: ausschließlich sessionStorage (endet mit dem Tab).
   Kein localStorage, kein Cookie, keine Serverübertragung, kein Analytics.
   Jeder Zugriff ist gekapselt: schlägt Storage fehl (Inkognito, strenger
   Datenschutzmodus, volles Kontingent), entfällt lediglich die Persistenz —
   die Suche bleibt vollständig benutzbar und die Konsole bleibt still.
   =========================================================================== */
(() => {
  if (window.__lmmSearchMemory) return; // idempotent: keine doppelten Listener

  const KEY = 'lmm:last-search-query';
  const VERSION = 1;
  /**
   * EIN Limit für alle drei Wege — Eingabe, Speichern und Wiederherstellen.
   * Die Suchfelder der Storefront tragen kein `maxlength`, und es existiert im
   * Theme kein anderes verbindliches Suchlimit (statisch über alle Themedateien
   * geprüft und an den gerenderten Feldern gemessen: `maxLength` = −1). Deshalb
   * gilt die Auftragsvorgabe von höchstens 255 Zeichen — vorher standen hier
   * 256 beim Schreiben gegen 255 beim Lesen, was einen von Hand auf exakt 256
   * Zeichen getippten Begriff beim nächsten Wiederherstellen um ein Zeichen
   * gekürzt hätte.
   */
  const LIMIT = 255;

  /** Der Speicher wird nur EINMAL zurückgeschrieben, wenn er gekappt werden musste. */
  let gekappt = false;

  const kappe = (wert) => {
    let v = String(wert == null ? '' : wert).slice(0, LIMIT);
    // Kein halbes Zeichen: ein durch die Kappung getrenntes Surrogatpaar
    // (Emoji) bliebe sonst als Ersatzzeichen stehen.
    if (/[\uD800-\uDBFF]$/.test(v)) v = v.slice(0, -1);
    return v;
  };

  const store = {
    read() {
      try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return '';
        const data = JSON.parse(raw);
        if (!data || data.v !== VERSION || typeof data.q !== 'string') return '';
        const q = kappe(data.q);
        // Der gespeicherte Wert stammt aus dem Speicher desselben Tabs und kann
        // dort von Hand manipuliert worden sein. Feld und Speicher dürfen nach
        // dem Kappen nicht auseinanderlaufen.
        if (q !== data.q && !gekappt) {
          gekappt = true;
          store.write(q);
        }
        return q;
      } catch {
        return '';
      }
    },
    write(value) {
      try {
        const v = kappe(value);
        if (!v) {
          sessionStorage.removeItem(KEY);
          return;
        }
        sessionStorage.setItem(KEY, JSON.stringify({ v: VERSION, q: v, s: 'search' }));
      } catch {
        /* Persistenz entfällt, Suche bleibt nutzbar */
      }
    },
    clear() {
      try {
        sessionStorage.removeItem(KEY);
      } catch {
        /* s. o. */
      }
    },
  };

  /** Alle Sucheingaben der Storefront (Header-Feld und die Suchmodal-Eingabe). */
  const fields = () => [
    ...document.querySelectorAll('predictive-search-component input[name="q"], input.lmmh-search__input'),
  ];

  /**
   * Das vorhandene Lösch-X gehört zur jeweiligen Suchkomponente. Sein Zustand
   * wird über genau dieselbe Eigenschaft gesetzt, die die Komponente selbst
   * benutzt (`resetButton.hidden` in #showResetButton/#hideResetButton) — es
   * entsteht keine zweite Schaltfläche und keine zweite Zuständigkeit.
   *
   * Nötig, weil die Komponente das X ausschließlich aus echten `input`-
   * Ereignissen ableitet. Ein wiederhergestellter Wert wird bewusst OHNE
   * `input` gesetzt (sonst liefe eine Suche an) — das X blieb deshalb
   * verborgen, bis der Kunde ein Zeichen tippte (auf MAIN gemessen).
   */
  const xZu = (field) => {
    const box = field.closest('predictive-search-component') || field.closest('form');
    return box ? box.querySelector('.lmm-predictive__reset, [ref="resetButton"]') : null;
  };
  const setzeX = (field, sichtbar) => {
    const x = xZu(field);
    if (x) x.hidden = !sichtbar;
  };

  /**
   * Priorität 1: der Suchbegriff der aktuellen Suchergebnisseite.
   * Auf `/search?q=…` ist der URL-Parameter maßgeblich; ein älterer
   * gespeicherter Begriff darf ihn niemals überschreiben.
   */
  const seitenQuery = () => {
    try {
      const u = new URL(location.href);
      if (!/(^|\/)search\/?$/.test(u.pathname)) return null;
      const q = u.searchParams.get('q');
      return q === null ? null : kappe(lmmIdentAnzeige(q));
    } catch {
      return null;
    }
  };

  /**
   * Der gespeicherte Wert stammt aus dem Speicher desselben Tabs und kann dort
   * von Hand manipuliert worden sein. Er wird deshalb BEIM LESEN begrenzt; die
   * normale Eingabe bleibt unberuehrt.
   *
   * Die Suchfelder der Storefront tragen KEIN maxlength und es existiert kein
   * anderes verbindliches Suchlimit (statisch ueber 817 Themedateien geprueft und
   * an 8 gerenderten Feldern gemessen: maxLength = -1). Deshalb gilt die
   * Auftragsvorgabe von hoechstens 255 Zeichen.
   */
  let last = null;
  let restored = false;
  let timer = null;
  /** Sobald der Kunde selbst tippt, hört jedes Nachfassen sofort auf. */
  let userTouched = false;

  const remember = (value) => {
    const trimmed = (value || '').trim();
    if (trimmed === last) return;
    last = trimmed;
    clearTimeout(timer);
    // Kein Schreiben bei jedem Tastendruck: gebündelt, wie die Suche selbst.
    timer = setTimeout(() => store.write(trimmed), 250);
  };

  /** Setzt den Wert OHNE input-Event — sonst liefe eine Suche oder ein Morph an. */
  const mirror = (value, except) => {
    for (const field of fields()) {
      if (field !== except && field.value !== value) field.value = value;
    }
  };

  const restore = () => {
    const seite = seitenQuery();
    const gespeichert = store.read();

    // Quellenpriorität: aktuelle Suchergebnisseite > Tab-Speicher > leer.
    if (seite !== null && seite !== '' && seite !== gespeichert) {
      // Der Speicher darf dem sichtbaren Suchbegriff der Seite nicht
      // widersprechen — sonst zeigte die nächste Seite den alten Begriff.
      last = seite;
      store.write(seite);
    }
    const value = seite !== null && seite !== '' ? seite : gespeichert;
    if (!value) return;

    for (const field of fields()) {
      // Serverwerte haben immer Vorrang. Auf /search?q=… rendert Liquid den
      // Suchbegriff als value-ATTRIBUT ins Feld; die Property kann während der
      // Initialisierung kurzzeitig leer sein. Deshalb beides prüfen — sonst
      // überschreibt das Nachfassen den echten Suchbegriff der Seite (auf
      // /search?q=osram+t5 gemessen, bevor diese Bedingung ergänzt wurde).
      if (field.value !== '' || (field.getAttribute('value') || '') !== '') {
        // Das Feld trägt bereits einen Wert — nur den X-Zustand nachziehen.
        setzeX(field, field.value !== '');
        continue;
      }
      // Reiner Text: field.value ist eine String-Property, es wird nichts als
      // HTML ausgewertet, nichts navigiert, nichts geöffnet, kein Panel
      // aufgeklappt und kein Fokus gesetzt.
      field.value = value;
      setzeX(field, true);
      restored = true;
    }
    if (restored) last = value;
  };

  const wipe = () => {
    clearTimeout(timer);
    last = '';
    store.clear();
    for (const field of fields()) {
      field.value = '';
      setzeX(field, false);
    }
  };

  document.addEventListener(
    'input',
    (event) => {
      const field = event.target;
      if (!(field instanceof HTMLInputElement) || field.name !== 'q') return;
      userTouched = true;
      const value = field.value;
      if (!value.trim()) {
        // Der Kunde hat selbst geleert → auch das Gedächtnis leeren.
        wipe();
        return;
      }
      remember(value);
      mirror(value, field);
      for (const other of fields()) setzeX(other, other.value !== '');
    },
    true
  );

  // Das vorhandene X: die bestehende resetSearch-Logik bleibt unangetastet,
  // hier kommt nur das Löschen des Gedächtnisses dazu.
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('.lmm-predictive__reset, [ref="resetButton"]')) wipe();
    },
    true
  );

  /*
   * Escape leert das Feld über die bestehende resetSearch-Logik der Komponente
   * — ohne `input`-Ereignis. Ohne diesen Zusatz bliebe der Begriff im Speicher
   * stehen und tauchte auf der nächsten Seite wieder auf (auf MAIN gemessen:
   * Feld '18W' → '', Speicher blieb {q:'18W'}). Das Leeren der Komponente läuft
   * zuerst, deshalb wird der Speicher im Anschluss nachgezogen.
   */
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      const field = event.target;
      if (!(field instanceof HTMLInputElement) || field.name !== 'q') return;
      setTimeout(wipe, 0);
    },
    true
  );

  // Beim aktiven Fokussieren steht der Cursor hinter dem wiederhergestellten Text.
  document.addEventListener(
    'focusin',
    (event) => {
      const field = event.target;
      if (!restored || !(field instanceof HTMLInputElement) || field.name !== 'q') return;
      restored = false;
      const end = field.value.length;
      try {
        field.setSelectionRange(end, end);
      } catch {
        /* type=search erlaubt setSelectionRange nicht überall */
      }
    },
    true
  );

  /**
   * Kein Fokus, keine Suche, kein geöffnetes Panel — nur der Feldwert.
   *
   * Mehrfach angestoßen, weil ein einzelner Zeitpunkt nachweislich nicht reicht:
   * ES-Module laufen vor DOMContentLoaded, der Browser stellt Formularwerte aber
   * erst danach wieder her und überschreibt dabei die frühe Zuweisung (auf der PDP
   * gemessen: Feld leer, obwohl der Speicher gefüllt war). Alle Aufrufe sind
   * idempotent — restore() rührt ausschließlich leere Felder an.
   */
  const restoreLater = () => {
    restore();
    requestAnimationFrame(() => requestAnimationFrame(restore));
  };

  restoreLater();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restoreLater);
  if (document.readyState !== 'complete') window.addEventListener('load', restoreLater);
  // Rückwärts-/Vorwärtsnavigation aus dem bfcache rendert kein neues Dokument.
  window.addEventListener('pageshow', restoreLater);

  /*
   * Ein einzelner Zeitpunkt reicht nachweislich nicht: Dieses Modul wird von der
   * Komponentenregistrierung geladen und läuft je nach Seitentyp, bevor das
   * Suchfeld in seiner endgültigen Fassung im DOM steht — DOMContentLoaded, load
   * und pageshow sind dann längst vorbei. Gemessen auf PDP und Collection:
   * Speicher gefüllt, Feld leer; ein manuell ausgelöstes pageshow füllte sofort
   * korrekt, und ein gesetzter Wert wurde über 3 s nicht wieder geleert — das
   * Feld war also schlicht noch nicht da bzw. wurde danach ersetzt.
   *
   * Deshalb wird kurz nachgefasst, bis der Kunde selbst tippt. Nach dem X oder
   * einem manuellen Leeren ist der Speicher leer und restore() tut nichts mehr —
   * ein geleertes Feld füllt sich also nicht wieder auf.
   */
  const NACHFASS_MS = 2500;
  const NACHFASS_TAKT = 100;
  const ende = Date.now() + NACHFASS_MS;
  const ticker = setInterval(() => {
    if (userTouched || Date.now() > ende) {
      clearInterval(ticker);
      return;
    }
    restore();
  }, NACHFASS_TAKT);

  window.__lmmSearchMemory = { key: KEY, version: VERSION, read: store.read, clear: wipe };
})();

